import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { BusinessCalendar, BusinessCalendarInput } from '@/api/adminApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FormError, ListState, RowActions, tdClass, thClass } from '@/components/admin/AdminShared'
import { useAdminResource } from '@/hooks/useAdmin'
import {
  EMPTY_CALENDAR_FORM,
  WEEKDAYS,
  calendarToForm,
  formToCalendar,
  formatDayHours,
  validateCalendar,
  type CalendarForm,
} from '@/lib/adminForms'

export function CalendarsTab() {
  const { t } = useTranslation()
  const { list, save, remove } = useAdminResource('calendars', true)
  const [editing, setEditing] = useState<BusinessCalendar | 'new' | null>(null)
  const [deleting, setDeleting] = useState<BusinessCalendar | null>(null)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('admin.add.calendars')}</Button>
      </div>
      <ListState query={list}>
        {list.data && list.data.length === 0 ? (
          <p className="py-6 text-meta text-ink-muted">{t('admin.empty')}</p>
        ) : (
          <table className="w-full border-collapse">
            <thead className="bg-surface-subtle">
              <tr>
                <th className={thClass}>{t('admin.calendar.name')}</th>
                <th className={thClass}>{t('admin.calendar.timeZone')}</th>
                <th className={thClass}>{t('admin.calendar.hours')}</th>
                <th className={thClass}>{t('admin.calendar.holidays')}</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {list.data?.map((c) => (
                <tr key={c.id} className="border-b border-line">
                  <td className={`${tdClass} font-medium text-ink-strong`}>{c.name}</td>
                  <td className={`${tdClass} font-mono`}>{c.timeZone}</td>
                  <td className={tdClass}>
                    {c.is24x7
                      ? t('admin.calendar.summary24x7')
                      : WEEKDAYS.filter((d) => (c.hours?.[d] ?? []).length > 0)
                          .map((d) => `${t(`admin.calendar.days.${d}`)} ${formatDayHours(c.hours[d])}`)
                          .join(' · ')}
                  </td>
                  <td className={tdClass}>{t('admin.calendar.holidayCount', { count: c.holidays?.length ?? 0 })}</td>
                  <td className={tdClass}>
                    <RowActions onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ListState>

      {editing ? (
        <CalendarDialog
          calendar={editing === 'new' ? null : editing}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) => save.mutate({ id: editing === 'new' ? undefined : editing.id, input }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title={t('admin.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('admin.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </div>
  )
}

function CalendarDialog({
  calendar,
  pending,
  error,
  onClose,
  onSave,
}: {
  calendar: BusinessCalendar | null
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: BusinessCalendarInput) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<CalendarForm>(() => (calendar ? calendarToForm(calendar) : EMPTY_CALENDAR_FORM))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const found = validateCalendar(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(formToCalendar(form))
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{calendar ? t('admin.editTitle.calendars') : t('admin.createTitle.calendars')}</DialogTitle>
          <DialogDescription>{t('admin.calendar.hoursHelp')}</DialogDescription>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          <Field id="cal-name" label={t('admin.calendar.name')} error={err('name')}>
            <Input id="cal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field id="cal-tz" label={t('admin.calendar.timeZone')} error={err('timeZone')} help={t('admin.calendar.timeZoneHelp')}>
            <Input id="cal-tz" className="font-mono" value={form.timeZone} onChange={(e) => setForm({ ...form, timeZone: e.target.value })} />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-meta text-ink">
            <input type="checkbox" checked={form.is24x7} onChange={(e) => setForm({ ...form, is24x7: e.target.checked })} />
            {t('admin.calendar.is24x7')}
          </label>
          {!form.is24x7 ? (
            <fieldset className="col-span-2 grid grid-cols-[48px_1fr] items-center gap-x-3 gap-y-2">
              <legend className="pb-2 text-meta font-medium text-ink">{t('admin.calendar.hours')}</legend>
              {WEEKDAYS.map((d) => (
                <div key={d} className="contents">
                  <label htmlFor={`cal-${d}`} className="text-meta text-ink-muted">
                    {t(`admin.calendar.days.${d}`)}
                  </label>
                  <div className="flex flex-col gap-1">
                    <Input
                      id={`cal-${d}`}
                      className="h-9 font-mono"
                      value={form.hours[d] ?? ''}
                      onChange={(e) => setForm({ ...form, hours: { ...form.hours, [d]: e.target.value } })}
                    />
                    {err(`hours.${d}`) ? <p className="text-label text-status-escalated-fg">{err(`hours.${d}`)}</p> : null}
                  </div>
                </div>
              ))}
              {err('hours') ? <p className="col-span-2 text-label text-status-escalated-fg">{err('hours')}</p> : null}
            </fieldset>
          ) : null}
          <Field id="cal-holidays" label={t('admin.calendar.holidays')} help={t('admin.calendar.holidaysHelp')} error={err('holidays')} className="col-span-2">
            <Textarea
              id="cal-holidays"
              rows={3}
              className="font-mono"
              value={form.holidays}
              onChange={(e) => setForm({ ...form, holidays: e.target.value })}
            />
          </Field>
          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
